import { Box, Modal } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  sendDigiatlBillViaSMS,
  sendDigiatlBillViaEmail,
  sendDigiatlBillViaWA,
} from "../../redux-store/slices/TableViewSlice";
import {
  ToastNotifyError,
  ToastNotifySuccess,
} from "commonComponents/ToastNotify";
import { getErrorMessage } from "utils/pos_errors";
import { EMAIL, SMS, WHATSAPP, COPY_LINK, SHARE_LINK, PRINT } from "utils/constants";
import { isValidEmail } from "utils/validator";
import { isValidPhoneNumber } from "utils/regex";
import { maskPhoneNumber } from "helper/commonHelpers";
import { handlePrintLogic } from "helper/printHelper";
import CommonInput from "commonComponents/CommonInput";
import { SEND_DIGITAL_BILL } from "utils/restrictionConstants";
import { featureRestrictions, ACCESS_DISABLED_MESSAGE } from "helper/featureRestrictionHelper";
import { useIsPostSaleCommunicationEnabled } from "hooks/commonHooks";


interface Props {
  onClose: () => void;
}

interface ActionTile {
  key: string;
  backendKey: string;
  label: string;
  icon: React.ReactNode;
}

export const PostSalePopup = ({ onClose }: Props) => {
  const saleData = useSelector((state: any) => state.checkoutModalStore.postSaleData);
  const { mediums: allowedMediums } = useIsPostSaleCommunicationEnabled();

  const billLink: string = saleData?.url || "";
  const mid: string = saleData?.mid || "";
  const businessName: string = saleData?.billHeader?.businessName || "";
  const customerPhone: string =
    saleData?.customerInfo?.contactInfo?.phoneNumber || "";
  const customerEmail: string =
    saleData?.customerInfo?.contactInfo?.email || "";
  const hasCustomerPhone: boolean = !!customerPhone;
  const hasCustomerEmail: boolean = !!customerEmail;

  const [selectedAction, setSelectedAction] = useState<string>("");
  const [contactNumber, setContactNumber] = useState<string>(customerPhone);
  const [emailVal, setEmailVal] = useState<string>(customerEmail);
  const [isEditingContact, setIsEditingContact] = useState<boolean>(false);
  const [isSendClicked, setIsSendClicked] = useState<boolean>(false);
  const [numberError, setNumberError] = useState<boolean>(false);
  const [emailErr, setEmailErr] = useState<boolean>(false);
  const [linkCopied, setLinkCopied] = useState<boolean>(false);
  const inputRef = useRef<any>(null);
  const dispatch = useDispatch();

  function handleActionSelect(action: string) {
    if (action === PRINT) {
      handlePrintLogic(saleData);
      return;
    }
    if ([SMS, WHATSAPP, EMAIL].includes(action)) {
      if (!featureRestrictions(SEND_DIGITAL_BILL)) {
        ToastNotifyError(ACCESS_DISABLED_MESSAGE);
        return;
      }
    }
    setSelectedAction(action);
    setIsSendClicked(false);
    setNumberError(false);
    setEmailErr(false);
    setIsEditingContact(false);
  }

  function handleEditContact() {
    setIsEditingContact(true);
    if (selectedAction === EMAIL) {
      setEmailVal("");
      setEmailErr(false);
    } else {
      setContactNumber("");
      setNumberError(false);
    }
    setIsSendClicked(false);
  }

  function handleSend() {
    setIsSendClicked(true);

    if (
      (selectedAction === EMAIL && !isValidEmail(emailVal)) ||
      ((selectedAction === SMS || selectedAction === WHATSAPP) &&
        (!contactNumber || !isValidPhoneNumber(contactNumber)))
    ) {
      if (selectedAction === EMAIL) setEmailErr(true);
      else setNumberError(true);
      return;
    }

    const callback = (response: any = {}) => {
      if (response?.sent) {
        const maskedNum =
          selectedAction !== EMAIL ? maskPhoneNumber(contactNumber) : emailVal;
        ToastNotifySuccess(
          getErrorMessage(`SENT_DIGITAL_BILL`, selectedAction, maskedNum)
        );
        onClose();
      } else {
        ToastNotifyError(
          response?.code ? response.code : getErrorMessage("GENERAL_OPERATION_FAILED_MSG")
        );
      }
    };

    if (selectedAction === SMS) {
      const payload = {
        amount: "INR " + saleData?.billTotal,
        mobileNumber: parseInt(contactNumber),
        isExternal: true,
        type: null,
        hasSMSProvider: true,
        businessName,
      };
      dispatch(sendDigiatlBillViaSMS({ mid, payload, callback }));
    } else if (selectedAction === WHATSAPP) {
      const payload = {
        businessName,
        isExternal: true,
        mobileNumber: parseInt(contactNumber),
        type: null,
      };
      dispatch(sendDigiatlBillViaWA({ mid, payload, callback }));
    } else if (selectedAction === EMAIL) {
      dispatch(
        sendDigiatlBillViaEmail({ mid, selectedContactNumber: emailVal, callback })
      );
    }
  }

  function handleCopyLink() {
    if (!billLink) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(billLink).then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      });
    }
  }

  function handleShareLink() {
    if (!billLink) return;
    if ((navigator as any).share) {
      (navigator as any)
        .share({ url: billLink, title: "Bill" })
        .catch(() => {});
    } else {
      handleCopyLink();
    }
  }

  const ACTION_TILES: ActionTile[] = [
    {
      key: PRINT,
      backendKey: "PRINT",
      label: "Print",
      icon: <PrintRoundedIcon style={{ fontSize: 28 }} />,
    },
    {
      key: SMS,
      backendKey: "SMS",
      label: "SMS",
      icon: <ChatBubbleOutlineRoundedIcon style={{ fontSize: 28 }} />,
    },
    {
      key: WHATSAPP,
      backendKey: "WHATSAPP",
      label: "WhatsApp",
      icon: <WhatsAppIcon style={{ fontSize: 28 }} />,
    },
    {
      key: EMAIL,
      backendKey: "EMAIL",
      label: "Email",
      icon: <MailOutlineRoundedIcon style={{ fontSize: 28 }} />,
    },
    {
      key: COPY_LINK,
      backendKey: "COPY_LINK",
      label: "Copy link",
      icon: <LinkRoundedIcon style={{ fontSize: 28 }} />,
    },
    {
      key: SHARE_LINK,
      backendKey: "SHARE_LINK",
      label: "Share link",
      icon: <IosShareRoundedIcon style={{ fontSize: 28 }} />,
    },
  ];

  const visibleTiles = ACTION_TILES.filter((tile) => allowedMediums.includes(tile.backendKey));

  const renderActionPanel = (
    label: string,
    contentNode: React.ReactNode,
    buttonNode: React.ReactNode,
    errorNode?: React.ReactNode,
    showEditButton?: boolean,
    onEdit?: () => void
  ) => {
    return (
      <section className="bg-[#f4f6fb] border-[1.5px] border-[#397ef5] rounded-xl px-6 pt-5 pb-8">
        <header className="flex items-center justify-between mb-3">
          <h3 className="text-[11px] font-bold tracking-[0.08em] text-[#777] uppercase m-0">{label}</h3>
        </header>
        <div className="flex items-center w-full gap-3">
          <div className="flex-1 min-w-0">
            {contentNode}
          </div>
          <div className="flex items-center gap-10 shrink-0">
            {showEditButton && onEdit && (
              <button
                className="text-[14px] font-semibold text-[#397ef5] bg-transparent border-none cursor-pointer p-0 hover:opacity-80"
                onClick={onEdit}
              >
                Edit
              </button>
            )}
            {buttonNode}
          </div>
        </div>
        {errorNode}
      </section>
    );
  };

  function renderContactPanel() {
    switch (selectedAction) {
      case SMS:
      case WHATSAPP:
        return renderActionPanel(
          selectedAction === SMS ? "SEND SMS TO" : "SEND ON WHATSAPP TO",
          hasCustomerPhone && !isEditingContact ? (
            <p className="text-[16px] font-semibold text-[#1a1a1a] flex-1 m-0 overflow-hidden text-ellipsis whitespace-nowrap">
              {maskPhoneNumber(contactNumber)}
            </p>
          ) : (
            <CommonInput
              elementRef={inputRef}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setNumberError(false);
                setIsSendClicked(false);
                const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
                setContactNumber(val);
              }}
              value={contactNumber}
              className="w-full border-none bg-transparent outline-none text-[15px] text-[#1a1a1a] h-9 px-1 min-w-0"
              otherProps={{
                type: "tel",
                placeholder:
                  selectedAction === WHATSAPP
                    ? "Enter WhatsApp Number"
                    : "Enter SMS Number",
                maxLength: 10,
              }}
            />
          ),
          <button
            className={`flex items-center justify-center bg-[#397ef5] text-white border-none rounded-lg py-2.5 px-6 text-[14px] font-semibold cursor-pointer whitespace-nowrap transition-opacity duration-150 shrink-0 hover:opacity-85${!contactNumber ? " opacity-40 pointer-events-none cursor-not-allowed" : ""
              }`}
            disabled={!contactNumber}
            onClick={handleSend}
          >
            <SendRoundedIcon style={{ fontSize: 16, marginRight: 6 }} />
            Send
          </button>,
          numberError && isSendClicked && (
            <p className="text-[12px] text-[#e53935] mt-1.5 mb-0">Enter valid number</p>
          ),
          hasCustomerPhone && !isEditingContact,
          handleEditContact
        );
      case EMAIL:
        return renderActionPanel(
          "SEND TO EMAIL",
          hasCustomerEmail && !isEditingContact ? (
            <p className="text-[16px] font-semibold text-[#1a1a1a] flex-1 m-0 overflow-hidden text-ellipsis whitespace-nowrap">
              {emailVal}
            </p>
          ) : (
            <CommonInput
              onChange={(e: any) => {
                setEmailErr(false);
                setIsSendClicked(false);
                setEmailVal(e.target.value);
              }}
              value={emailVal}
              className="w-full border-none bg-transparent outline-none text-[15px] text-[#1a1a1a] h-9 px-1 min-w-0 font-semibold"
              otherProps={{ type: "email", placeholder: "Enter Email" }}
            />
          ),
          <button
            className={`flex items-center justify-center bg-[#397ef5] text-white border-none rounded-lg py-2.5 px-6 text-[14px] font-semibold cursor-pointer whitespace-nowrap transition-opacity duration-150 shrink-0 hover:opacity-85${!emailVal ? " opacity-40 pointer-events-none cursor-not-allowed" : ""
              }`}
            disabled={!emailVal}
            onClick={handleSend}
          >
            <SendRoundedIcon style={{ fontSize: 16, marginRight: 6 }} />
            Send
          </button>,
          emailErr && isSendClicked && (
            <p className="text-[12px] text-[#e53935] mt-1.5 mb-0">Enter valid email</p>
          ),
          hasCustomerEmail && !isEditingContact,
          handleEditContact
        );
      case COPY_LINK:
        return renderActionPanel(
          "BILL LINK",
          <p className="text-[16px] font-semibold text-[#1a1a1a] flex-1 overflow-hidden text-ellipsis whitespace-nowrap m-0">
            {billLink || "—"}
          </p>,
          <button
            className={`flex items-center justify-center bg-[#397ef5] text-white border-none rounded-lg py-2.5 px-6 text-[14px] font-semibold cursor-pointer whitespace-nowrap transition-opacity duration-150 shrink-0 hover:opacity-85${!billLink ? " opacity-40 pointer-events-none cursor-not-allowed" : ""
              }`}
            disabled={!billLink}
            onClick={handleCopyLink}
          >
            <ContentCopyRoundedIcon style={{ fontSize: 16, marginRight: 6 }} />
            {linkCopied ? "Copied!" : "Copy"}
          </button>
        );
      case SHARE_LINK:
        return renderActionPanel(
          "SHARE BILL LINK",
          <p className="text-[16px] font-semibold text-[#1a1a1a] flex-1 overflow-hidden text-ellipsis whitespace-nowrap m-0">
            {billLink || "—"}
          </p>,
          <button
            className={`flex items-center justify-center bg-[#397ef5] text-white border-none rounded-lg py-2.5 px-6
                        text-[14px] font-semibold cursor-pointer whitespace-nowrap transition-opacity duration-150 shrink-0 hover:opacity-85${!billLink ? " opacity-40 pointer-events-none cursor-not-allowed" : ""
              }`}
            disabled={!billLink}
            onClick={handleShareLink}
          >
            <SendRoundedIcon style={{ fontSize: 16, marginRight: 6 }} />
            Share
          </button>
        );
      default:
        return null;
    }
  }

  return (
    <Modal
      className="flex justify-center items-center p-10"
      open={true}
      onClose={onClose}
    >
      <Box style={{ outline: 0 }} component="article" className="bg-white rounded-[32px] w-[520px] max-w-[95vw] shadow-[0_8px_32px_rgba(0,0,0,0.18)] flex flex-col relative pb-12">
        {/* Cross Button */}
        <button
          className="absolute -right-[60px] top-0 flex items-center justify-center p-3 rounded-full bg-[#969696] text-white border-none cursor-pointer hover:bg-gray-500 z-10"
          onClick={onClose}
        >
          <CloseRoundedIcon sx={{ width: "28px", height: "28px", fontWeight: "900", color: "#ffffff" }} />
        </button>

        {/* Header */}
        <header className="flex items-center justify-between px-12 pt-10 pb-6">
          <div className="flex items-center gap-[16px]">
            <div className="flex items-center justify-center w-[54px] h-[54px] rounded-full bg-[#e6f7ec] shrink-0">
              <CheckCircleRoundedIcon
                style={{ fontSize: 46, color: "#25A244" }}
              />
            </div>
            <div>
              <h2 className="text-[20px] font-bold text-[#1a1a1a] leading-snug m-0">Sale Completed</h2>
              <p className="text-[14px] text-[#888] mt-1 mb-0">
                Send the bill or print it
              </p>
            </div>
          </div>
        </header>

        <hr className="h-px bg-[#ebebeb] border-0 m-0" />

        {/* Action tiles grid */}
        <section className="grid grid-cols-3 gap-4 px-12 pt-15 pb-15">
          {visibleTiles.map((tile) => {
            const isSelected = selectedAction === tile.key;
            return (
              <button
                key={tile.key}
                className={`flex flex-col items-center justify-center gap-4 py-6 px-4 mr-1.5 ml-1.5 mt-1.5 mb-1 rounded-[20px] border-[1.5px] bg-white cursor-pointer transition-colors duration-150 min-h-[116px] hover:bg-[#f5f5f5] hover:border-[#ccc] ${isSelected ? " !bg-[#eef3fe] !border-[#397ef5]" : " border-[#e4e4e4]"
                  }`}
                onClick={() => handleActionSelect(tile.key)}
              >
                <span className="flex items-center justify-center text-inherit">{tile.icon}</span>
                <span className="text-[14px] font-medium text-[#333] text-center">{tile.label}</span>
              </button>
            );
          })}
        </section>

        {/* Contextual panel */}
        {selectedAction && (
          <section className="px-12 pt-5">
            {renderContactPanel()}
          </section>
        )}
      </Box>
    </Modal>
  );
};